import { HttpResponse } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';

import { ClientAdminApiService } from 'src/app/api/services/client-admin-api.service';
import { Injectable, inject } from '@angular/core';
import { UtilsService } from './utils.service';

export interface CompanyData {
  emailAddress?: string;
  rootPassword?: string;
  rootPassword2?: string;
  companyAccountTypeId: string;
  companyClientTypeId: string;
  profitCenterId: string;
  partnerAgencyId: string;
  companyId?: string | null;
  companyEnabled: boolean;
  description: string;
  companyName: string;
  companyAlias: string;
  vat: string;
  addressLine1: string;
  addressLine2: string;
  countryId: string;
  city: string;
  phone: string;
  fax: string;
  companyWebsiteUrl: string;

  defaultCompanyAdminRoleTemplateId?: string;
  defaultCompanyUserRoleTemplateId?: string;
  companyPeriodStartDateRelative: string;
  companyPeriodStartDate: string;
  companyPeriodStartEnd: string;

  // for company limits
  feed_type_id: any;
  status: any;
  max_number: any;
}

export interface CompanyUIData {
  companyId: string;
  companyChartColorScheme: string[];
  companyChartHeatMapColorScheme: string[];
}

export interface CompanySystemData {
  companyId: string;
  companyCustomDomain: string;
  mailServerHost: string;
  mailServerPort: string;
  mailServerUsername: string;
  mailServerPassword: string;
  mailServerSecurity: string;
  mailServerFromEmail: string;
  mailServerFromName: string;
}

export interface KeyValuePair {
  key: number;
  value: number;
}

export interface CompanyLimitsRequests {
  status: KeyValuePair[];
  feedTypeId: KeyValuePair[];
  featureTypeId: KeyValuePair[];
  maxNumber: KeyValuePair[];
}

/**
 * Company client-side layer: the admin grids' CSV-download wrappers and the
 * company editor form types. All backend transport lives in
 * `CompanyApiService` / `ClientAdminApiService`.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private clientAdminApi = inject(ClientAdminApiService);
  private utilService = inject(UtilsService);

  /**
   * Fetches the companies AgGrid model; the 'csv' output downloads the export
   * in the browser instead of returning grid data.
   */
  fetchCompaniesAgGridModel(
    gridParams: any,
    output: 'table' | 'csv',
  ): Observable<any> {
    if (output === 'csv') {
      return this.clientAdminApi.getCompaniesAgGridModel(gridParams, 'csv').pipe(
        tap((res: HttpResponse<Blob>) => {
          const fileName = this.utilService.getFileNameFromDisposition(
            res.headers.get('content-disposition'),
            'companies.csv',
          );
          this.utilService.downloadBlob(res.body!, fileName);
        }),
        // caller usually doesn't need grid data when exporting
        map(() => ({ success: true })),
      );
    }

    return this.clientAdminApi.getCompaniesAgGridModel(gridParams, 'table');
  }

  fetchExportSchedulesAgGridModel(
    gridParams: any,
    output: 'table' | 'csv',
  ): Observable<any> {
    if (output === 'csv') {
      return this.clientAdminApi.getSchedulesAgGridModel(gridParams, 'csv').pipe(
        tap((res: HttpResponse<Blob>) => {
          const fileName = this.utilService.getFileNameFromDisposition(
            res.headers.get('content-disposition'),
            'companies.csv',
          );
          this.utilService.downloadBlob(res.body!, fileName);
        }),
        // caller usually doesn't need grid data when exporting
        map(() => ({ success: true })),
      );
    }

    return this.clientAdminApi.getSchedulesAgGridModel(gridParams, 'table');
  }
}
